import { logErrorToSentry, ErrorLevel } from './';
import showdown from 'showdown';
import sanitizeHtml from 'sanitize-html';

// Security extensions (based on Jane's implementation)
const createSecurityExtensions = () => {
  return [
    // Remove javascript from links
    {
      type: 'lang',
      regex: /\[(.*)\](.*)javascript(.*)/gi,
      replace: '$1 [BLOCKED]',
    },
    // Add security attributes to links
    {
      type: 'output',
      regex: /<a(?![^>]*target)/g,
      replace: '<a target="_blank" rel="noopener noreferrer"',
    },
    // Add styling classes
    {
      type: 'output',
      regex: /<a(?![^>]*class)/g,
      replace: '<a class="underline"',
    },
    // Make images responsive
    {
      type: 'output',
      regex: /<img/g,
      replace: '<img class="img-responsive inline-block"',
    },
  ];
};

// Sanitization config (matching Jane's whitelist)
const sanitizeConfig = {
  allowedTags: [
    'strong',
    'em',
    'a',
    'p',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ol',
    'ul',
    'li',
    'img',
    'hr',
    'br',
  ],
  allowedAttributes: {
    '*': ['id', 'class'],
    a: ['href', 'target', 'rel', 'class'],
    img: ['src', 'alt', 'class'],
  },
  disallowedTagsMode: 'discard' as sanitizeHtml.DisallowedTagsModes,
  allowedSchemes: ['http', 'https', 'mailto'],
  allowProtocolRelative: true,
};

interface MarkdownConverterOptions {
  extensions: string[];
  headerLevelStart: number;
  simplifiedAutoLink: boolean;
  excludeTrailingPunctuationFromURLs: boolean;
  strikethrough: boolean;
  tables: boolean;
  tasklists: boolean;
  ghCodeBlocks: boolean;
  smoothLivePreview: boolean;
}

/**
 * Renders markdown text to sanitized HTML
 * @param markdownText - The markdown text to render
 * @returns Sanitized HTML string
 */
export const renderMarkdown = (
  markdownText: string | null | undefined
): string => {
  if (!markdownText || typeof markdownText !== 'string') {
    return '';
  }

  try {
    showdown.extension('securityExtensions', createSecurityExtensions());

    const converter: showdown.Converter = new showdown.Converter({
      extensions: ['securityExtensions'],
      headerLevelStart: 2,
      simplifiedAutoLink: true,
      excludeTrailingPunctuationFromURLs: true,
      strikethrough: true,
      tables: false, // Disable tables for security
      tasklists: false, // Disable task lists
      ghCodeBlocks: false, // Disable code blocks
      smoothLivePreview: true,
    } as MarkdownConverterOptions);
    const rawHtml: string = converter.makeHtml(markdownText);
    const sanitizedHtml: string = sanitizeHtml(rawHtml, sanitizeConfig);

    return sanitizedHtml;
  } catch (error) {
    logErrorToSentry({
      type: ErrorLevel.Error,
      message: `Error rendering markdown. ${error}`,
    });

    return markdownText.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
};
